import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';

const Cart = () => {
  const { cartItems, removeFromCart, updateQuantity, getTotal, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [packCount, setPackCount] = useState(0);
  const [deliveryFee, setDeliveryFee] = useState(5.00);
  const [paymentMethod, setPaymentMethod] = useState('livraison');

  // ✅ AJOUT : Vérifier si l'utilisateur est connecté au chargement de la page
  useEffect(() => {
    if (!user) {
      // Sauvegarder l'URL actuelle pour rediriger après connexion
      sessionStorage.setItem('redirectAfterLogin', '/cart');
      navigate('/login');
      return;
    }
  }, [user, navigate]);

  useEffect(() => {
    const totalPacks = cartItems.reduce((sum, item) => sum + item.quantity, 0);
    setPackCount(totalPacks);
    
    if (totalPacks <= 5) setDeliveryFee(9.90);
    else if (totalPacks <= 10) setDeliveryFee(14.90);
    else if (totalPacks <= 20) setDeliveryFee(19.90);
    else if (totalPacks <= 40) setDeliveryFee(29.90);
    else setDeliveryFee(0);
  }, [cartItems]);

  const subtotal = getTotal();
  const totalTTC = subtotal + deliveryFee;
  const minOrder = 4;

  const handleCheckout = () => {
    if (packCount < minOrder) {
      alert(`Minimum ${minOrder} articles pour valider la commande. Vous avez actuellement ${packCount} pack(s).`);
      return;
    }
    
    // ✅ AJOUT : Vérification supplémentaire avant checkout
    if (!user) {
      sessionStorage.setItem('redirectAfterLogin', '/checkout');
      navigate('/login');
      return;
    }
    
    navigate('/checkout', { state: { paymentMethod, totalTTC, deliveryFee, subtotal } });
  };

  // Reste du code inchangé...
  const Icons = {
    Trash: () => (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
      </svg>
    ),
    Minus: () => (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
      </svg>
    ),
    Plus: () => (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
    ),
    CreditCard: () => (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
      </svg>
    ),
    Cash: () => (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    )
  };

  // ✅ AJOUT : Si l'utilisateur n'est pas connecté, ne pas afficher le panier
  if (!user) {
    return null; // Le useEffect va rediriger
  }

  if (cartItems.length === 0) {
    return (
      <div className="container-custom py-20 text-center">
        <div className="max-w-md mx-auto">
          <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icons.CreditCard />
          </div>
          <h1 className="text-2xl font-bold mb-4">Votre panier est vide</h1>
          <p className="text-gray-500 mb-8">Découvrez nos produits d'exception</p>
          <Link to="/products" className="btn-primary inline-block">Découvrir la boutique</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container-custom py-12">
      <h1 className="text-3xl font-bold mb-2">Panier</h1>
      {packCount < 4 && (
        <p className="text-red-500 text-sm mb-6">⚠ Minimum 4 articles pour valider la commande. Vous avez actuellement {packCount} pack(s).</p>
      )}

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Produit</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Prix</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Quantité</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Sous-total</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold"></th>
                  </tr>
                </thead>
                <tbody>
                  {cartItems.map((item) => (
                    <tr key={item.id} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                            <img 
                              src={item.image || '/default-product.png'} 
                              alt={item.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div>
                            <p className="font-medium text-darkGray">{item.name}</p>
                            <p className="text-xs text-gray-400 mt-1">{item.format || '6 x 1,25L'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-primary font-bold">{item.price.toFixed(2)} €</td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2 border rounded-lg w-fit">
                          <button onClick={() => updateQuantity(item.id, item.quantity - 1)} className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-l-lg">
                            <Icons.Minus />
                          </button>
                          <span className="w-8 text-center text-sm">{item.quantity}</span>
                          <button onClick={() => updateQuantity(item.id, item.quantity + 1)} className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-r-lg">
                            <Icons.Plus />
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-4 font-bold text-primary">{(item.price * item.quantity).toFixed(2)} €</td>
                      <td className="px-4 py-4">
                        <button onClick={() => removeFromCart(item.id)} className="text-red-400 hover:text-red-600">
                          <Icons.Trash />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          <div className="mt-4 flex justify-between items-center">
            <button onClick={clearCart} className="text-red-500 text-sm hover:underline flex items-center gap-1">
              <Icons.Trash /> Vider le panier
            </button>
            <Link to="/products" className="text-secondary hover:underline text-sm">
              ← Continuer mes achats
            </Link>
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-md p-6 sticky top-24">
            <h2 className="text-xl font-bold mb-4">Total panier</h2>
            
            <div className="space-y-3 border-b pb-4">
              <div className="flex justify-between">
                <span className="text-gray-600">Sous-total</span>
                <span className="font-medium">{subtotal.toFixed(2)} €</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Livraison</span>
                <span className="font-medium">{deliveryFee.toFixed(2)} €</span>
              </div>
            </div>
            
            <div className="flex justify-between py-4 border-b mb-4">
              <span className="text-lg font-bold">Total TTC</span>
              <span className="text-xl font-bold text-primary">{totalTTC.toFixed(2)} €</span>
            </div>

            <div className="mb-6">
              <h3 className="font-semibold mb-3">Mode de paiement</h3>
              <div className="space-y-3">
                <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input type="radio" name="paymentMethod" value="livraison" checked={paymentMethod === 'livraison'} onChange={(e) => setPaymentMethod(e.target.value)} className="w-4 h-4 text-secondary" />
                  <Icons.Cash />
                  <div>
                    <p className="font-medium">Payer à la livraison</p>
                    <p className="text-xs text-gray-500">Règlement en espèces ou carte directement au livreur</p>
                  </div>
                </label>
                
                <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input type="radio" name="paymentMethod" value="online" checked={paymentMethod === 'online'} onChange={(e) => setPaymentMethod(e.target.value)} className="w-4 h-4 text-secondary" />
                  <Icons.CreditCard />
                  <div>
                    <p className="font-medium">Payer en ligne</p>
                    <p className="text-xs text-gray-500">Paiement sécurisé par carte bancaire (un lien de paiement vous sera envoyer par email)</p>
                  </div>
                </label>
              </div>
            </div>

            <button onClick={handleCheckout} className={`w-full py-3 rounded-full font-semibold transition ${packCount >= 4 ? 'bg-primary text-white hover:bg-opacity-90' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`} disabled={packCount < 4}>
              Valider la commande
            </button>
            
            {packCount < 4 && (
              <p className="text-xs text-red-500 text-center mt-3">
                Ajoutez {4 - packCount} pack(s) supplémentaire(s) pour valider la commande
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;